import { useCallback, useSyncExternalStore, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Save } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  flushDirtyCards,
  hasDirtyCards,
  subscribeDirtyCards,
} from '@/features/patient-record/lib/dirtyRegistry';
import { selectAnySaving, useAutosaveStatusStore } from '@/features/patient-record/lib/autosaveStatus';

interface SaveButtonProps {
  /** `null` = the patient record; a guid narrows to one node. */
  readonly scopeId?: string | null;
  readonly className?: string;
}

/**
 * PTV2-1327 — the unified save button for the patient-record header, matching the pedigree toolbar's
 * Save button: an icon + short label showing **Saving… / Unsaved / Saved** (green check). Clicking it
 * while there are unsaved changes commits **every** dirty section now ("save all"), mirroring the
 * pedigree's manual Save. When clean it's a passive green-check "Saved".
 */
export function SaveButton({ scopeId = null, className }: SaveButtonProps): ReactElement {
  const { t } = useTranslation();
  const getDirty = useCallback(() => hasDirtyCards(scopeId), [scopeId]);
  const dirty = useSyncExternalStore(subscribeDirtyCards, getDirty);
  const saving = useAutosaveStatusStore((s) => selectAnySaving(s, scopeId));

  const saveAll = (): void => {
    flushDirtyCards({ scopeId });
  };

  let icon: ReactElement;
  let label: string;
  let clickable = false;
  if (saving) {
    icon = <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />;
    label = t('patient.autosave.saving', { defaultValue: 'Saving…' });
  } else if (dirty) {
    icon = (
      <span className="relative inline-flex">
        <Save className="h-4 w-4" aria-hidden="true" />
        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border-[1.5px] border-background bg-amber-400" />
      </span>
    );
    label = t('patient.autosave.unsaved', { defaultValue: 'Unsaved' });
    clickable = true;
  } else {
    icon = <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />;
    label = t('patient.autosave.saved', { defaultValue: 'Saved' });
  }

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={clickable ? saveAll : undefined}
      title={clickable ? t('patient.autosave.saveAll', { defaultValue: 'Save all changes' }) : label}
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-medium leading-none transition-colors',
        clickable
          ? 'cursor-pointer border border-border text-foreground hover:bg-accent/60'
          : 'cursor-default text-muted-foreground',
        className,
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
